import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { PageConstraints } from "../design-system/constraints/pageConstraints";

export const useCanvas = ({ editMode, resumeData }) => {
  const location = useLocation();
  const resumeAreaRef = useRef(null);
  const resumeInnerRef = useRef(null);
  const [resumeScale, setResumeScale] = useState(0.68);
  const [manualZoom, setManualZoom] = useState(null); // null = auto-fit, number = manual
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const panOffsetStartRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef(null);

  // Dynamic scale — ResizeObserver with RAF throttling to prevent resize loops
  const computeScale = useCallback(() => {
    if (!resumeAreaRef.current) return;
    const containerH = resumeAreaRef.current.clientHeight - 32;
    const containerW = resumeAreaRef.current.clientWidth - 48;
    if (containerH <= 50 || containerW <= 50) return; // Guard against unmounted/zero-size states during reflows
    const naturalW = PageConstraints?.A4_WIDTH || 794;
    // Enforce standard A4 height (1123px) as minimum natural height to preserve portrait aspect ratio in empty states
    const naturalH = Math.max(PageConstraints?.A4_HEIGHT || 1123, resumeInnerRef.current?.scrollHeight || 0);
    const scaleByW = containerW / naturalW;
    const scaleByH = containerH / naturalH;
    setResumeScale((prev) => {
      const next = Math.min(scaleByW, scaleByH, 1);
      // Avoid unnecessary re-renders if scale hasn't meaningfully changed
      return Math.abs(prev - next) > 0.001 ? next : prev;
    });
  }, []);

  // Throttled version of computeScale for ResizeObserver — RAF + no inner ref observation
  const throttledComputeScale = useCallback(() => {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      computeScale();
    });
  }, [computeScale]);

  useEffect(() => {
    let ro;
    let checkIntervalId;

    const initObserver = () => {
      if (resumeAreaRef.current) {
        ro = new ResizeObserver(throttledComputeScale);
        ro.observe(resumeAreaRef.current);
        clearInterval(checkIntervalId);
        computeScale();
      }
    };

    // Try immediately on mount
    initObserver();

    // If the ref isn't populated yet, check periodically (Ref race condition guard)
    if (!resumeAreaRef.current) {
      checkIntervalId = setInterval(initObserver, 50);
    }

    window.addEventListener("resize", throttledComputeScale);

    return () => {
      if (ro) ro.disconnect();
      if (checkIntervalId) clearInterval(checkIntervalId);
      window.removeEventListener("resize", throttledComputeScale);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [throttledComputeScale, computeScale]); // Only recreate observer on mount — NOT on every data change

  // Recompute scale progressively when resume data changes to fit pagination heights
  // and handle delayed rendering of templates and fonts on initial load
  useEffect(() => {
    const t1 = setTimeout(computeScale, 50);
    const t2 = setTimeout(computeScale, 200);
    const t3 = setTimeout(computeScale, 500);
    const t4 = setTimeout(computeScale, 1000);
    const t5 = setTimeout(computeScale, 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [resumeData, location.pathname, computeScale]);

  // ── Ctrl+= / Ctrl+- keyboard zoom shortcuts ──
  useEffect(() => {
    const onKey = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        setManualZoom((prev) => Math.min(1.5, parseFloat(((prev ?? resumeScale) + 0.1).toFixed(2))));
      } else if (e.key === '-') {
        e.preventDefault();
        setManualZoom((prev) => Math.max(0.3, parseFloat(((prev ?? resumeScale) - 0.1).toFixed(2))));
      } else if (e.key === '0') {
        e.preventDefault();
        setManualZoom(null);
        setPanOffset({ x: 0, y: 0 }); // reset pan on Ctrl+0
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [resumeScale]);

  // ── Drag-to-pan handlers (Eraser.io canvas style) ──
  const handleCanvasMouseDown = useCallback((e) => {
    // Allow drag on left-click background (not on the resume card itself)
    // editMode: still pan on right-click / middle-click so editing works
    if (editMode && e.button === 0) return; // left-click in edit mode → no pan (let editable fields work)
    if (e.button === 2) return; // ignore right-click (context menu)
    e.preventDefault();
    isPanningRef.current = true;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    panOffsetStartRef.current = { ...panOffset };
    document.body.style.cursor = 'grabbing';
  }, [panOffset, editMode]);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setPanOffset({
      x: panOffsetStartRef.current.x + dx,
      y: panOffsetStartRef.current.y + dy,
    });
  }, []);

  const handleCanvasMouseUp = useCallback(() => {
    if (!isPanningRef.current) return;
    isPanningRef.current = false;
    document.body.style.cursor = '';
  }, []);

  // Reset pan when switching to auto-fit
  useEffect(() => {
    if (manualZoom === null) setPanOffset({ x: 0, y: 0 });
  }, [manualZoom]);

  const effectiveZoom = manualZoom ?? resumeScale;

  return {
    resumeScale,
    setResumeScale,
    manualZoom,
    setManualZoom,
    panOffset,
    setPanOffset,
    isPanningRef,
    resumeAreaRef,
    resumeInnerRef,
    effectiveZoom,
    handleCanvasMouseDown,
    handleCanvasMouseMove,
    handleCanvasMouseUp,
  };
};
